import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { categories } from '../../data/categories';
import SearchIcon from '../search/SearchIcon';

const Header = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const { getCartCount, setIsCartOpen } = useCart();

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Handle search
  const handleSearch = (query) => {
    // Optional: You can add analytics or other logic here
    console.log('Searching for:', query);
  };

  // Toggle category expansion in mobile menu
  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  // Close mobile menu and reset expansion
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setExpandedCategory(null);
  };

  return (
    <>
      {/* Main Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-2">
          <div className="flex items-center justify-between h-20">
            
            {/* Left: Logo & Title */}
            <Link to="/" className="flex items-center gap-3 group" onClick={closeMobileMenu}>
              <div className="transition-transform duration-300 group-hover:scale-105">
                <img 
                  src="/images/logo/skyf-logo.webp" 
                  alt="Sky Furniture Logo" 
                  className="h-16 w-auto rounded-2xl"
                />
              </div>
              <div className="hidden sm:block text-center">
                <h1 className="text-xl font-display font-bold text-dark-800 leading-tight">
                  SKY FURNITURE
                </h1>
                <p className="text-xl font-display font-bold text-dark-800 leading-tight">CANADA</p>
              </div>
            </Link>

            {/* Center: Categories Navigation (Desktop) */}
            <nav className="hidden lg:flex items-center gap-1">
              {categories.slice(0, 5).map((category) => (
                <div
                  key={category.id}
                  className="relative"
                  onMouseEnter={() => setActiveDropdown(category.id)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={`/products?category=${category.slug}`}
                    className="flex items-center gap-1 px-4 py-4 text-sm font-medium text-dark-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                  >
                    {category.name}
                    {category.subcategories.length > 0 && (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Link>

                  {category.subcategories.length > 0 && activeDropdown === category.id && (
                    <div className="absolute top-full left-0 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 animate-fade-in">
                      {category.subcategories.map((sub) => (
                        <Link
                          key={sub.id}
                          to={`/products?category=${category.slug}&subcategory=${sub.slug}`}
                          className="block px-4 py-2 text-sm text-dark-600 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                          onClick={() => setActiveDropdown(null)}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* More Categories */}
              <div
                className="relative"
                onMouseEnter={() => setActiveDropdown('more')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-dark-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200">
                  More
                  <ChevronDown className="w-4 h-4" />
                </button>

                {activeDropdown === 'more' && (
                  <div className="absolute top-full left-0 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 animate-fade-in">
                    {categories.slice(5).map((category) => (
                      <Link
                        key={category.id}
                        to={`/products?category=${category.slug}`}
                        className="block px-4 py-2 text-sm text-dark-600 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                        onClick={() => setActiveDropdown(null)}
                      >
                        {category.name}
                      </Link>
                    ))}
                    <div className="border-t border-gray-100 my-2"></div>
                    <Link
                      to="/shop"
                      className="block px-4 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 hover:bg-accent-50 transition-colors duration-200"
                      onClick={() => setActiveDropdown(null)}
                    >
                      View All Categories →
                    </Link>
                  </div>
                )}
              </div>
            </nav>

            {/* Right: Search, Cart, Menu Icons */}
            <div className="flex items-center gap-2">
              
              {/* Expandable Search Component */}
              <SearchIcon
                onSearch={handleSearch}
              />

              {/* Cart Icon with Badge */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-2.5 hover:bg-primary-50 rounded-lg transition-colors duration-200"
                aria-label="Shopping Cart"
              >
                <ShoppingCart className="w-5 h-5 text-dark-700" />
                {getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-scale-in">
                    {getCartCount()}
                  </span>
                )}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2.5 hover:bg-primary-50 rounded-lg transition-colors duration-200"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6 text-dark-700" />
                ) : (
                  <Menu className="w-6 h-6 text-dark-700" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu - Full Screen Overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-20 bg-white z-40 animate-slide-down">
            <div className="h-full overflow-y-auto overscroll-contain">
              <div className="container mx-auto px-4 py-4">
                
                {/* Mobile Categories - Accordion Style */}
                <nav className="space-y-1">
                  {categories.map((category) => (
                    <div key={category.id} className="border-b border-gray-100 last:border-b-0">
                      {/* Category Header */}
                      <div className="flex items-center justify-between">
                        <Link
                          to={`/products?category=${category.slug}`}
                          className="flex-1 px-4 py-3 text-sm font-medium text-dark-700 hover:bg-primary-50 rounded-lg transition-colors"
                          onClick={closeMobileMenu}
                        >
                          {category.name}
                        </Link>
                        
                        {category.subcategories.length > 0 && (
                          <button
                            onClick={() => toggleCategory(category.id)}
                            className="p-3 hover:bg-primary-50 rounded-lg transition-colors"
                            aria-label={`${expandedCategory === category.id ? 'Collapse' : 'Expand'} ${category.name}`}
                            aria-expanded={expandedCategory === category.id}
                          >
                            {expandedCategory === category.id ? (
                              <ChevronDown className="w-5 h-5 text-dark-600" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-dark-600" />
                            )}
                          </button>
                        )}
                      </div>
                      
                      {/* Subcategories */}
                      {category.subcategories.length > 0 && expandedCategory === category.id && (
                        <div className="ml-4 mb-2 space-y-1 animate-slide-down">
                          {category.subcategories.map((sub) => (
                            <Link
                              key={sub.id}
                              to={`/products?category=${category.slug}&subcategory=${sub.slug}`}
                              className="block px-4 py-2 text-sm text-dark-600 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors"
                              onClick={closeMobileMenu}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* View All Categories */}
                  <div className="pt-2 mt-2">
                    <Link
                      to="/products"
                      className="block px-4 py-3 text-sm font-medium text-accent-600 hover:bg-accent-50 rounded-lg transition-colors text-center"
                      onClick={closeMobileMenu}
                    >
                      View All Categories →
                    </Link>
                  </div>
                </nav>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Spacer to prevent content from hiding under fixed header */}
      <div className="h-20" />
    </>
  );
};

export default Header;